import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────
//  Página pública (sin login). La familia abre el enlace tokenizado del email
//  y autoriza/deniega que se hagan fotos a sus hijos y se publiquen en redes.
//  Token en la query (?token=…). Funciona para reservas web y nativas: el
//  backend resuelve dónde guardar el cambio.
// ─────────────────────────────────────────────────────────────

export default function EditarImagen() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [phase, setPhase] = useState('loading'); // loading | error | form | saved
  const [error, setError] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [campistas, setCampistas] = useState([]); // [{key, nombre, autoriza}]
  const [guardando, setGuardando] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setError('Enlace no válido.'); setPhase('error'); return; }
    try {
      const r = await api.getImgConsent(token);
      setFamilyName(r.familyName || '');
      setCampistas((r.campistas || []).map(c => ({ ...c, autoriza: !!c.autoriza })));
      if (!r.campistas || r.campistas.length === 0) {
        setError('Esta reserva todavía no tiene campistas con datos.');
        setPhase('error');
      } else {
        setPhase('form');
      }
    } catch (e) {
      setError(e.message || 'No se pudo cargar el enlace. Puede haber caducado.');
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setCampistas(cs => cs.map(c => c.key === key ? { ...c, autoriza: !c.autoriza } : c));

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.saveImgConsent(token, campistas.map(c => ({ key: c.key, autoriza: c.autoriza })));
      setPhase('saved');
    } catch (e) {
      setError(e.message || 'No se pudo guardar. Inténtalo de nuevo.');
      setPhase('error');
    } finally {
      setGuardando(false);
    }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#1a1612', color: '#f2ead8', fontFamily: 'Georgia, serif', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h1 style={{ color: '#c8820a', fontSize: '1.5rem', marginBottom: '.2rem' }}>📷 Autorización de imágenes</h1>
        <p style={{ color: '#8a8070', fontSize: '.85rem', marginTop: 0, marginBottom: '1.8rem' }}>Aldealix la Aldea Celta</p>
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') return wrap(<p style={{ color: '#b0a898' }}>Cargando…</p>);

  if (phase === 'error') return wrap(
    <div style={{ background: '#221e19', borderLeft: '3px solid #b5482f', padding: '20px', borderRadius: 2 }}>
      <p style={{ margin: 0, color: '#f2ead8' }}>⚠️ {error}</p>
    </div>
  );

  if (phase === 'saved') return wrap(
    <div style={{ background: '#221e19', borderLeft: '3px solid #5a7a50', padding: '24px', borderRadius: 2 }}>
      <p style={{ margin: 0, color: '#f2ead8', fontSize: '1.05rem' }}>✅ ¡Guardado! Gracias.</p>
      <p style={{ color: '#b0a898', lineHeight: 1.7, marginBottom: 0 }}>Hemos registrado tu preferencia sobre las fotos. Puedes volver a este enlace para cambiarla cuando quieras.</p>
    </div>
  );

  // phase === 'form'
  return wrap(
    <>
      <p style={{ color: '#b0a898', lineHeight: 1.7 }}>
        {familyName ? <>Hola, <strong style={{ color: '#f2ead8' }}>{familyName}</strong>: </> : null}
        marca a quién <strong style={{ color: '#f2ead8' }}>autorizas</strong> que se le hagan fotos durante el campamento y se publiquen en las redes sociales de Aldealix. Desmarca a quien <strong style={{ color: '#f2ead8' }}>no</strong> quieras que aparezca.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem', margin: '1.4rem 0' }}>
        {campistas.map(c => (
          <label key={c.key} style={{
            display: 'flex', alignItems: 'center', gap: '.8rem', cursor: 'pointer',
            background: '#221e19', border: `1px solid ${c.autoriza ? '#5a7a50' : '#3a332c'}`,
            padding: '14px 16px', borderRadius: 6,
          }}>
            <input type="checkbox" checked={c.autoriza} onChange={() => toggle(c.key)}
              style={{ width: 20, height: 20, accentColor: '#5a7a50', flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              <span style={{ color: '#f2ead8', fontWeight: 600 }}>{c.nombre || 'Campista'}</span>
              <span style={{ display: 'block', fontSize: '.82rem', color: c.autoriza ? '#8bbf7a' : '#b08a72' }}>
                {c.autoriza ? '✅ Autorizo las fotos' : '🚫 No autorizo las fotos'}
              </span>
            </span>
          </label>
        ))}
      </div>

      <button onClick={guardar} disabled={guardando}
        style={{
          display: 'block', width: '100%', textAlign: 'center', background: '#c8820a',
          color: '#1a1612', padding: '14px', borderRadius: 6, border: 'none',
          fontWeight: 700, fontSize: '1rem', cursor: guardando ? 'default' : 'pointer',
          opacity: guardando ? 0.7 : 1, fontFamily: 'inherit',
        }}>
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
      <p style={{ color: '#8a8070', fontSize: '.78rem', marginTop: '1rem', lineHeight: 1.6 }}>
        Puedes volver a este enlace y cambiar tu decisión cuando quieras.
      </p>
    </>
  );
}
